import React, { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { Search, MapPin, Loader2 } from "lucide-react";
import { mapsApi } from "../../lib/api";
import { DEFAULT_CENTER } from "../../lib/constants";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const pinGIcon = () => {
  const g = window.google.maps;
  return {
    url:
      "data:image/svg+xml;charset=UTF-8," +
      encodeURIComponent(
        `<svg viewBox="0 0 28 36" width="32" height="42" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.3 0 0 6.2 0 13.9 0 24.3 14 36 14 36s14-11.7 14-22.1C28 6.2 21.7 0 14 0z" fill="#1D4ED8"/><circle cx="14" cy="14" r="5.5" fill="#FFFFFF"/></svg>`,
      ),
    scaledSize: new g.Size(32, 42),
    anchor: new g.Point(16, 40),
  };
};

// Clamp a point to within radiusM of the constrain centre.
function clamp(la, ln, constrain) {
  if (!constrain || !Number.isFinite(constrain.lat)) return [la, ln];
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(la - constrain.lat), dLng = toRad(ln - constrain.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(constrain.lat)) * Math.cos(toRad(la)) * Math.sin(dLng / 2) ** 2;
  const dist = 2 * R * Math.asin(Math.sqrt(a));
  if (dist > constrain.radiusM) {
    const f = constrain.radiusM / dist;
    return [constrain.lat + (la - constrain.lat) * f, constrain.lng + (ln - constrain.lng) * f];
  }
  return [la, ln];
}

function PickerLayer({ lat, lng, constrain, onPick }) {
  const map = useMap();
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  // Draggable marker + click-to-place + constraint circle.
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google.maps;
    const marker = new g.Marker({ position: { lat, lng }, icon: pinGIcon(), map, draggable: true });
    markerRef.current = marker;
    marker.addListener("dragend", (e) => {
      const [la, ln] = clamp(e.latLng.lat(), e.latLng.lng(), constrain);
      marker.setPosition({ lat: la, lng: ln });
      onPick(la, ln);
    });
    const clickL = map.addListener("click", (e) => {
      const [la, ln] = clamp(e.latLng.lat(), e.latLng.lng(), constrain);
      marker.setPosition({ lat: la, lng: ln });
      onPick(la, ln);
    });
    if (constrain && Number.isFinite(constrain.lat)) {
      circleRef.current = new g.Circle({
        center: { lat: constrain.lat, lng: constrain.lng },
        radius: constrain.radiusM,
        map,
        strokeColor: "#1D4ED8",
        strokeWeight: 1,
        fillColor: "#1D4ED8",
        fillOpacity: 0.08,
      });
    }
    return () => {
      marker.setMap(null);
      if (circleRef.current) circleRef.current.setMap(null);
      g.event.removeListener(clickL);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Reflect external lat/lng changes (search selection) onto the marker + viewport.
  useEffect(() => {
    if (!map || !markerRef.current) return;
    markerRef.current.setPosition({ lat, lng });
    map.panTo({ lat, lng });
    if (map.getZoom() < 15) map.setZoom(15);
  }, [map, lat, lng]);

  return null;
}

// Controlled location picker: Google Places search + draggable Google Maps pin.
export default function LocationPicker({ value, onChange, constrain }) {
  const lat = Number.isFinite(value?.lat) ? value.lat : DEFAULT_CENTER.lat;
  const lng = Number.isFinite(value?.lng) ? value.lng : DEFAULT_CENTER.lng;
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const sessionRef = useRef(Math.random().toString(36).slice(2));
  const debounceRef = useRef(null);

  useEffect(() => { mapsApi.config().then((c) => setEnabled(c.maps_enabled)).catch(() => {}); }, []);

  const runSearch = (text) => {
    setQuery(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await mapsApi.autocomplete(text, sessionRef.current);
        setSuggestions(r.suggestions || []);
        setOpen(true);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
  };

  const pickSuggestion = async (s) => {
    setOpen(false);
    setQuery(s.text);
    try {
      const p = await mapsApi.place(s.place_id);
      if (Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
        const [la, ln] = clamp(p.lat, p.lng, constrain);
        onChange({ lat: la, lng: ln, address: p.address || s.text });
      }
    } catch { /* ignore */ }
    sessionRef.current = Math.random().toString(36).slice(2);
  };

  return (
    <div className="space-y-3" data-testid="location-picker">
      <div className="relative">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => runSearch(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder={enabled ? "Search your restaurant's address…" : "Address search unavailable"}
            disabled={!enabled}
            className="w-full rounded-xl border border-surface-300 bg-white py-2.5 pl-10 pr-9 text-sm text-ink outline-none focus:border-brand"
            data-testid="location-search-input"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-muted" />}
        </div>
        {open && suggestions.length > 0 && (
          <ul className="absolute z-[1000] mt-1 max-h-64 w-full overflow-auto rounded-xl border border-surface-300 bg-white shadow-lg" data-testid="location-suggestions">
            {suggestions.map((s) => (
              <li key={s.place_id}>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }} className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface-100" data-testid="location-suggestion">
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" />
                  <span>{s.text}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-surface-300" style={{ height: 300 }} data-testid="location-map">
        {KEY ? (
          <APIProvider apiKey={KEY}>
            <Map
              defaultCenter={{ lat, lng }}
              defaultZoom={15}
              style={{ width: "100%", height: "100%", minHeight: 300 }}
              gestureHandling="greedy"
              disableDefaultUI
              zoomControl
              clickableIcons={false}
            >
              <PickerLayer
                lat={lat}
                lng={lng}
                constrain={constrain}
                onPick={(la, ln) => onChange({ lat: la, lng: ln, address: value?.address || "" })}
              />
            </Map>
          </APIProvider>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-ink-muted">Map unavailable</div>
        )}
      </div>
      <p className="text-xs text-ink-muted">Tap the map or drag the pin to set your exact location.{constrain ? ` Must be within ${constrain.radiusM} m of your chosen point.` : ""} Pin: {lat.toFixed(5)}, {lng.toFixed(5)}</p>
    </div>
  );
}
